/**
 * SwipeButton - Swipe-to-Confirm Button
 *
 * Für kritische Aktionen auf Mobile: User muss nach rechts swipen um zu bestätigen.
 * Verhindert versehentliches Antippen.
 *
 * TODO (Tablet/Desktop): Schönen Confirm-Dialog implementieren statt Swipe.
 * Aktuell wird auf größeren Screens auch Swipe verwendet.
 */

import { useState, useRef, useCallback } from 'preact/hooks';
import { Check, ChevronRight } from 'lucide-preact';
import styles from './SwipeButton.module.css';

export type SwipeButtonVariant = 'primary' | 'success' | 'danger' | 'secondary';

interface SwipeButtonProps {
  /** Text der angezeigt wird */
  label: string;
  /** Text während des Ladens */
  loadingLabel?: string;
  /** Callback wenn Swipe abgeschlossen */
  onConfirm: () => void | Promise<void>;
  /** Button deaktiviert */
  disabled?: boolean;
  /** Lädt gerade */
  loading?: boolean;
  /** Farbvariante */
  variant?: SwipeButtonVariant;
}

/** Mindest-Swipe-Distanz als Prozent der Button-Breite */
const SWIPE_THRESHOLD = 0.7; // 70%

export function SwipeButton({
  label,
  loadingLabel,
  onConfirm,
  disabled = false,
  loading = false,
  variant = 'primary',
}: SwipeButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const dragXRef = useRef(0); // Ref für aktuellen Wert in Callbacks

  const getMaxDrag = useCallback(() => {
    if (!containerRef.current) return 200;
    // Thumb-Breite (48px) abziehen
    return containerRef.current.offsetWidth - 56;
  }, []);

  const handleStart = useCallback(() => {
    if (disabled || loading || isConfirmed) return;
    setIsDragging(true);
  }, [disabled, loading, isConfirmed]);

  const handleMove = useCallback((clientX: number, startX: number) => {
    if (!isDragging || disabled || loading) return;

    const maxDrag = getMaxDrag();
    const newX = Math.max(0, Math.min(clientX - startX, maxDrag));
    dragXRef.current = newX; // Ref aktualisieren für handleEnd
    setDragX(newX);
  }, [isDragging, disabled, loading, getMaxDrag]);

  const handleEnd = useCallback(async () => {
    if (!isDragging) return;
    setIsDragging(false);

    const maxDrag = getMaxDrag();
    const threshold = maxDrag * SWIPE_THRESHOLD;
    const currentDragX = dragXRef.current; // Aktuellen Wert aus Ref lesen

    if (currentDragX >= threshold) {
      // Swipe erfolgreich
      setIsConfirmed(true);
      setDragX(maxDrag);

      try {
        await onConfirm();
      } finally {
        // Reset nach kurzer Verzögerung
        setTimeout(() => {
          setIsConfirmed(false);
          setDragX(0);
          dragXRef.current = 0;
        }, 500);
      }
    } else {
      // Zurück zum Start
      setDragX(0);
      dragXRef.current = 0;
    }
  }, [isDragging, getMaxDrag, onConfirm]);

  // Touch Events
  const touchStartX = useRef(0);

  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX - dragX;
    handleStart();
  };

  const onTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    handleMove(touch.clientX, touchStartX.current);
  };

  const onTouchEnd = () => {
    void handleEnd();
  };

  // Mouse Events (für Desktop-Testing)
  const mouseStartX = useRef(0);

  const onMouseDown = (e: MouseEvent) => {
    mouseStartX.current = e.clientX - dragX;
    handleStart();

    // Global listeners für drag außerhalb des Elements
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    handleMove(e.clientX, mouseStartX.current);
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    void handleEnd();
  };

  const progress = dragX / getMaxDrag();
  const displayLabel = loading && loadingLabel ? loadingLabel : label;

  const variantClass = styles[variant] ?? '';

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${variantClass} ${disabled ? styles.disabled : ''} ${isConfirmed ? styles.confirmed : ''}`}
    >
      {/* Hintergrund-Track */}
      <div className={styles.track}>
        <div
          className={styles.trackFill}
          style={{ width: `${progress * 100}%` }}
        />
        <span className={styles.trackLabel}>
          {isConfirmed ? 'Bestätigt!' : displayLabel}
        </span>
        <div className={styles.trackHint}>
          <ChevronRight size={16} />
          <ChevronRight size={16} style={{ marginLeft: '-8px', opacity: 0.5 }} />
        </div>
      </div>

      {/* Draggable Thumb */}
      <div
        className={`${styles.thumb} ${isDragging ? styles.dragging : ''}`}
        style={{ transform: `translateX(${dragX}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
      >
        {loading ? (
          <div className={styles.spinner} />
        ) : isConfirmed ? (
          <Check size={24} />
        ) : (
          <ChevronRight size={24} />
        )}
      </div>
    </div>
  );
}
